import {
  BlueprintQuoteScope,
  CollaborationAccessEnum,
  ContractStatusEnum,
  DiscountBreakdownField,
  EffectiveDateType,
  IQuoteOfferingReqSchema,
  IQuoteRequestSchema,
  NetTermsEnum,
  NewQuoteTypeEnum,
  ProductTypeEnum,
  QuoteCancelationReasonEnum,
  QuoteStartDateSourceEnum,
  QuoteStatusEnum,
  QuoteTypeEnum,
  QuoteTypeEnumDisplay,
  RateBillingFrequencyEnum,
} from '@monetize/types/app';
import { objectToSelectOptions } from '../utils/misc';

// Statuses of quotes that we can still approve/rejects/retract
export const QUOTE_APPROVE_ACTIONABLE_STATUSES = new Set([
  QuoteStatusEnum.REVIEW,
  QuoteStatusEnum.APPROVED,
  QuoteStatusEnum.DENIED,
  QuoteStatusEnum.SENT,
  QuoteStatusEnum.ACCEPTED,
]);

export const QUOTE_EDIT_ACTIONABLE_STATUSES = new Set([
  QuoteStatusEnum.REVIEW,
  QuoteStatusEnum.APPROVED,
  QuoteStatusEnum.DENIED,
  QuoteStatusEnum.SENT,
]);

export const QUOTE_READONLY_STATUSES = new Set([
  QuoteStatusEnum.SENT,
  QuoteStatusEnum.ACCEPTED,
  QuoteStatusEnum.PROCESSED,
  QuoteStatusEnum.CANCELED,
  QuoteStatusEnum.EXPIRED,
  QuoteStatusEnum.ARCHIVED,
]);

export const QUOTE_PRIMARY_BILLING_CONTACT_EDITABLE_STATUSES = new Set([
  QuoteStatusEnum.DRAFT,
  QuoteStatusEnum.REVIEW,
  QuoteStatusEnum.APPROVED,
  QuoteStatusEnum.ACCEPTED,
  QuoteStatusEnum.ACCEPTED_ADMIN_EDITABLE,
]);

export const QUOTE_ESIGN_CONTACT_EDITABLE_STATUSES = new Set([
  QuoteStatusEnum.DRAFT,
  QuoteStatusEnum.REVIEW,
  QuoteStatusEnum.APPROVED,
]);

export const QUOTE_ESIGN_CONTACT_RETRACTABLE_STATUSES = new Set([
  QuoteStatusEnum.SENT,
  QuoteStatusEnum.EXPIRED,
  QuoteStatusEnum.CANCELED,
]);

export const QUOTE_COPYABLE_STATUSES = new Set([
  QuoteStatusEnum.EXPIRED,
  QuoteStatusEnum.SENT,
  QuoteStatusEnum.DENIED,
  QuoteStatusEnum.APPROVED,
  QuoteStatusEnum.DRAFT,
  QuoteStatusEnum.REVIEW,
  QuoteStatusEnum.ARCHIVED,
]);

export const QUOTE_ARCHIVE_ACTION_STATUSES = new Set([
  QuoteStatusEnum.CANCELED,
]);

export const QUOTE_SUBMITTED_STATUSES = new Set([
  QuoteStatusEnum.REVIEW,
  QuoteStatusEnum.SENT,
  QuoteStatusEnum.ACCEPTED,
  QuoteStatusEnum.ACCEPTED_ADMIN_EDITABLE,
  QuoteStatusEnum.CANCELED,
  QuoteStatusEnum.PROCESSED,
  QuoteStatusEnum.DENIED,
  QuoteStatusEnum.APPROVED,
  QuoteStatusEnum.EXPIRED,
  QuoteStatusEnum.ARCHIVED,
]);

export const QUOTE_SENT_STATUSES = new Set([
  QuoteStatusEnum.SENT,
  QuoteStatusEnum.ACCEPTED,
  QuoteStatusEnum.ACCEPTED_ADMIN_EDITABLE,
  QuoteStatusEnum.PROCESSED,
]);

export const QUOTE_ACCEPTED_STATUSES = new Set([
  QuoteStatusEnum.ACCEPTED,
  QuoteStatusEnum.ACCEPTED_ADMIN_EDITABLE,
  QuoteStatusEnum.PROCESSED,
]);

export const QUOTE_EDITABLE_STATUSES = new Set([
  QuoteStatusEnum.DRAFT,
  QuoteStatusEnum.ACCEPTED_ADMIN_EDITABLE,
]);

export const QUOTE_DESCRIPTION_EDITABLE_STATUSES = new Set([
  QuoteStatusEnum.DRAFT,
  QuoteStatusEnum.REVIEW,
  QuoteStatusEnum.APPROVED,
  QuoteStatusEnum.ACCEPTED_ADMIN_EDITABLE,
]);

export const QUOTE_APPROVED_STATUSES = new Set([
  QuoteStatusEnum.SENT,
  QuoteStatusEnum.ACCEPTED,
  QuoteStatusEnum.PROCESSED,
  QuoteStatusEnum.APPROVED,
]);

export const QUOTE_MANUAL_RENEWABLE_STATUSES = new Set([
  QuoteStatusEnum.PROCESSED,
  QuoteStatusEnum.CANCELED,
  QuoteStatusEnum.EXPIRED,
]);

export const QUOTE_STATUES_TO_ALLOW_EXTEND_EXPIRATION_DATE = new Set([
  QuoteStatusEnum.REVIEW,
  QuoteStatusEnum.SENT,
  QuoteStatusEnum.APPROVED,
]);

export const QUOTE_NOT_ACTIONABLE_STATUSES = new Set([
  QuoteStatusEnum.CANCELED,
  QuoteStatusEnum.ARCHIVED,
  QuoteStatusEnum.PROCESSED,
  QuoteStatusEnum.EXPIRED,
]);

export const QUOTE_DISABLED_STATUSES = new Set([
  QuoteStatusEnum.CANCELED,
  QuoteStatusEnum.ARCHIVED,
  QuoteStatusEnum.EXPIRED,
]);

export const QUOTE_AFTER_SENT_STATUSES = new Set([
  QuoteStatusEnum.SENT,
  QuoteStatusEnum.ACCEPTED,
  QuoteStatusEnum.ACCEPTED_ADMIN_EDITABLE,
  QuoteStatusEnum.CANCELED,
  QuoteStatusEnum.PROCESSED,
  QuoteStatusEnum.DENIED,
  QuoteStatusEnum.EXPIRED,
  QuoteStatusEnum.ARCHIVED,
]);

export const CAN_SHARE_QUOTE_INTERNALLY_STATUSES = new Set([
  QuoteStatusEnum.DRAFT,
  QuoteStatusEnum.REVIEW,
  QuoteStatusEnum.APPROVED,
]);

export const CAN_SHARE_QUOTE_EXTERNALLY_STATUSES = new Set([
  QuoteStatusEnum.APPROVED,
  QuoteStatusEnum.SENT,
  QuoteStatusEnum.ACCEPTED,
]);

export const SHARE_QUOTE_EXTERNALLY_DRAFT_STATUSES = new Set([
  QuoteStatusEnum.DRAFT,
  QuoteStatusEnum.REVIEW,
]);

/**
 * Onetime charges. On an amendment these are locked on any offering that
 * carried over from the contract being amended - they can only be sold on an
 * offering the amendment itself adds.
 */
export const ONETIME_CHARGE_PRODUCT_TYPES = new Set([
  ProductTypeEnum.ONETIME,
  ProductTypeEnum.ONETIME_PREPAID_CREDIT,
]);

export const CAN_SET_PRIMARY_QUOTE_STATUSES = new Set([
  QuoteStatusEnum.DRAFT,
  QuoteStatusEnum.SENT,
  QuoteStatusEnum.APPROVED,
  QuoteStatusEnum.DENIED,
  QuoteStatusEnum.REVIEW,
]);

export const QUOTE_CREATE_DEAL_ROOM_STATUSES = new Set([
  QuoteStatusEnum.DRAFT,
  QuoteStatusEnum.APPROVED,
]);

export const QUOTE_TOTAL_EDIT_STYLE = {
  [QuoteTypeEnum.NEW]: {
    borderColor: undefined,
    backgroundColor: 'tGray.support',
    color: 'tPurple.dark',
  },
  [QuoteTypeEnum.AMENDMENT]: {
    borderColor: undefined,
    backgroundColor: 'tBlue.hover',
    color: 'tPurple.safety',
  },
  [QuoteTypeEnum.RENEWAL]: {
    borderColor: undefined,
    backgroundColor: 'tBlue.hover',
    color: 'tPurple.safety',
  },
};

export const QuoteStatusEnumDisplay: {
  [key in QuoteStatusEnum]: string;
} = {
  DRAFT: 'Draft',
  REVIEW: 'Review',
  APPROVED: 'Approved',
  DENIED: 'Denied',
  SENT: 'Sent',
  ACCEPTED: 'Accepted',
  ACCEPTED_ADMIN_EDITABLE: 'Accepted (Editable)',
  PROCESSED: 'Processed',
  CANCELED: 'Canceled',
  EXPIRED: 'Expired',
  ARCHIVED: 'Archived',
};

export const QuoteStatusTagStyle: {
  [key in QuoteStatusEnum]: {
    color: string;
    bgColor?: string;
    px?: number;
    pr?: number;
    borderRadius?: number;
    fontStyle?: string;
  };
} = {
  DRAFT: {
    color: 'tGray.darkGrayPurple',
    bgColor: 'transparent',
  },
  REVIEW: {
    color: 'tGray.darkGrayPurple',
  },
  APPROVED: {
    color: 'tGray.darkGrayPurple',
  },
  DENIED: {
    color: 'tRed.base',
  },
  SENT: {
    color: 'tGray.darkGrayPurple',
  },
  ACCEPTED: {
    color: 'tGreen.mDarkShade',
  },
  ACCEPTED_ADMIN_EDITABLE: {
    color: 'tGreen.mDarkShade',
  },
  PROCESSED: {
    color: 'tGreen.mDarkShade',
    bgColor: 'tGreen.light',
    px: 2,
    borderRadius: 4,
  },
  CANCELED: {
    color: 'tGray.darkGrayPurple',
  },
  EXPIRED: {
    color: 'tGray.darkGrayPurple',
    fontStyle: 'italic',
    // Italic glyphs overhang the layout box; without a sliver of right padding
    // the cell's `maxW="fit-content"` clips the trailing "d".
    pr: 1,
  },
  ARCHIVED: {
    color: 'tGray.darkGrayPurple',
  },
};

export const DISCOUNT_OPTIONS = [
  { title: '+Custom', value: 'CUSTOM' },
  { title: 'Monthly Sale', value: 'MONTHLY_SALE' },
  { title: 'First Customer', value: 'FIRST_CUSTOMER' },
];

export const DEFAULT_QUOTE_COLORS = {
  color: 'tGray.darkGrayPurple',
  bgColor: 'tWhite.base',
  borderColor: 'tGray.lightPurple',
};

export const BLUEPRINT_QUOTE_SCOPE_DISPLAY: {
  [key in BlueprintQuoteScope]: string;
} = {
  [BlueprintQuoteScope.USER]: 'Just Me',
  [BlueprintQuoteScope.TENANT]: 'All Users',
};
export const BLUEPRINT_QUOTE_SCOPE_OPTIONS = objectToSelectOptions(
  BLUEPRINT_QUOTE_SCOPE_DISPLAY,
);

// Edit/New Quote Variables

export const QUOTE_CUSTOM_CONTRACT_LENGTHS = [12, 24, 36];
export const QUOTE_CONTRACT_LENGTHS = [1, 2, 3, 4, 5];

export const QUOTE_GRID_TEMPLATE_COLUMNS = '4fr 1.5fr repeat(4, 1fr)  0.05fr';

/**
 * Width of every control that can render in the quantity column — the number
 * input, the tier dropdown, and the amendment addition input. The column is a
 * `1fr` grid track, so without a cap the dropdown stretched to the full track
 * on wide screens while the number input shrank to its content, leaving the two
 * visibly mismatched side by side. Matches the discount column's cap
 * (`MAmountWithUnitTypeInput` `maxWidth`) so the row reads as one grid.
 */
export const QUOTE_QUANTITY_CELL_WIDTH = '7rem';

/**
 * The quantity wrapper is a single centered track of QUOTE_QUANTITY_CELL_WIDTH
 * rather than a capped block, so the control centers in its column without auto
 * margins — those would push the incremental-quantity tooltip away from the
 * input it belongs to. `minmax(0, …)` lets it shrink on narrow screens.
 */
export const QUOTE_QUANTITY_CELL_GRID_TEMPLATE = `minmax(0, ${QUOTE_QUANTITY_CELL_WIDTH})`;

/**
 * Text inset for the quantity column's controls, so the value sits in the same
 * place in every state: an editable field puts it at 1px border + the Input
 * theme's 10px `sm` padding, while the `readonly` variant drops the border to
 * none and the padding to 0, so it has to carry the whole 11px itself.
 * QuantityText matches with `ps="11px"`.
 */
export const QUOTE_QUANTITY_TEXT_INSET = {
  paddingInlineStart: '10px',
  /**
   * `css`, not `_readOnly`: a `_readOnly` style prop replaces the readonly
   * variant's entire block — `border: none`, `user-select: all`, the forced
   * transparent background — rather than just its `padding-left: 0`. This
   * selector has the same specificity and is emitted after the variant's, so it
   * wins on declaration order and leaves the rest of the variant intact.
   * (v3 dropped `sx`; passed here it rendered as a literal DOM attribute.)
   */
  css: { '&[readonly]': { paddingInlineStart: '11px' } },
};

/**
 * Width of the Rate field on an expanded offering segment, whether it renders
 * as a select or as read-only text. Fixing it keeps the Start Date and End
 * Date columns of every segment in line — a read-only segment's text used to
 * size to the rate name, so its dates drifted relative to the editable
 * segments above and below it. Wide enough that most rate names fit; longer
 * ones truncate with a tooltip.
 */
export const QUOTE_RATE_FIELD_WIDTH = '13rem';

/** Shared width of the Start Date and End Date fields beside the Rate field */
export const QUOTE_DATE_FIELD_WIDTH = '10.25rem';

export const HEADER_ROW_CELLS = [
  'Offering',
  '', // do now show the header
  'Total Quantity',
  'Discount',
  'Price Per Unit',
  'Amount',
  '',
];

export const quoteOfferingInitial: IQuoteOfferingReqSchema = {
  offeringId: '',
  rateId: '',
  subscriptionId: '',
  discountIds: [],
  items: [],
};

export const INITIAL_FORM_VALUES: IQuoteRequestSchema = {
  description: '',
  currency: 'USD',
  netTerms: NetTermsEnum.NET_0,
  autoRenewContract: false,
  requiresEsign: false,
  collaborationAccess: CollaborationAccessEnum.NONE,
  legalEntityId: '',
  customFields: {},
  billingAnchor: null,
};

export const INITIAL_CONTACT_VALUES = {
  address: {
    city: '',
    country: '',
    description: '',
    id: '',
    line1: '',
    line2: '',
    postalCode: '',
    state: '',
  },
  customId: '',
  email: '',
  fullName: '',
  id: '',
  phone: '',
  status: '',
  type: 'QUOTE',
};

export const RATE_BILLING_FREQUENCY_DISPLAY: {
  [key in RateBillingFrequencyEnum]: string;
} = {
  DAILY: 'Daily',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  SEMIANNUALLY: 'Semi-annually',
  ANNUALLY: 'Annually',
  CUSTOM: 'Custom',
  ONETIME: 'Onetime',
};

export const PAPER_PAGE_MAX_WIDTH = 1220;

export const INITIAL_MANUAL_RENEWAL_VALUES = {
  type: NewQuoteTypeEnum.MANUAL_RENEWAL,
  previousArr: '',
};

export const QUITE_TYPE_SELECT_OPTIONS =
  objectToSelectOptions(QuoteTypeEnumDisplay);

export const NEW_CONTRACT_SLUG = '*/newContract/*';

export const CONTRACT_STATUS_AMENDMENT_ALLOWED = new Set([
  ContractStatusEnum.ACTIVE,
  ContractStatusEnum.PENDING,
]);

export const FREQUENCY_ITEMS = [
  { title: 'Monthly (1)', value: 1 },
  { title: 'Quarterly (3)', value: 3 },
  { title: 'Semi-Annually (6)', value: 6 },
  { title: 'Annually (12)', value: 12 },
];

export const FREQUENCY_ITEMS_WITHOUT_NUMBERINGS = [
  { title: 'Monthly', value: 1 },
  { title: 'Quarterly', value: 3 },
  { title: 'Semi-Annually', value: 6 },
  { title: 'Annually', value: 12 },
];

export const DISPLAY_FREQUENCY_ALLOWED_PRODUCT_TYPE = new Set([
  ProductTypeEnum.ADVANCE,
  ProductTypeEnum.MIN_COMMIT,
]);

export const QUOTE_AFFINITY = 'QUOTE_AFFINITY';
export const QUOTE_AFFINITY_MARCH_2024 = 'QUOTE_AFFINITY_MARCH_2024';
export const QUOTE_MATERIALIZE = 'QUOTE_MATERIALIZE';
export const QUOTE_MIDDESK = 'QUOTE_MIDDESK';
export const QUOTE_PLOTLINE = 'QUOTE_PLOTLINE';
export const QUOTE_DANFOSS_EU = 'QUOTE_DANFOSS_EU';
export const QUOTE_DANFOSS_US = 'QUOTE_DANFOSS_US';
export const QUOTE_LENDER = 'QUOTE_LENDER';
export const QUOTE_SIRAYA = 'QUOTE_SIRAYA';
export const QUOTE_SENTRY = 'QUOTE_SENTRY';
export const QUOTE_PROPER = 'QUOTE_PROPER';
export const QUOTE_DESCOPE = 'QUOTE_DESCOPE';
export const QUOTE_TELEPORT = 'QUOTE_TELEPORT';
export const QUOTE_PAVE = 'QUOTE_PAVE';
export const QUOTE_PAVE_ARR = 'QUOTE_PAVE_ARR';
export const QUOTE_XAGE = 'QUOTE_XAGE';
export const QUOTE_SCHOOX = 'QUOTE_SCHOOX';
export const QUOTE_ACUITYMD = 'QUOTE_ACUITYMD';
export const QUOTE_OPTIO = 'QUOTE_OPTIO';
export const QUOTE_COAST = 'QUOTE_COAST';
export const QUOTE_LIFTED = 'QUOTE_LIFTED';
export const QUOTE_FIRST_DUE = 'QUOTE_FIRST_DUE';
export const QUOTE_APP_CUES = 'QUOTE_APP_CUES';
export const QUOTE_STARTREE = 'QUOTE_STARTREE';
export const QUOTE_ENTR = 'QUOTE_ENTR';
export const QUOTE_RILLET = 'QUOTE_RILLET';
export const QUOTE_TEMPO = 'QUOTE_TEMPO';
export const QUOTE_FEXA = 'QUOTE_FEXA';
export const QUOTE_AGENT_SYNC = 'QUOTE_AGENT_SYNC';
export const QUOTE_MONETIZENOW = 'QUOTE_MONETIZENOW';

export const QUOTE_TEMPLATE_ITEMS: {
  title: string;
  value: string;
  /**
   * Indicates if this template should be available for everyone
   */
  isDefault?: true;
}[] = [
  { title: 'Default', value: 'QUOTE_DEFAULT_1', isDefault: true },
  {
    title: 'Default - Services Order Form',
    value: 'QUOTE_DEFAULT_2',
    isDefault: true,
  },
  {
    title: 'Default - Excludes VAT',
    value: 'QUOTE_DEFAULT_3_EXCL_VAT',
    isDefault: true,
  },
  {
    title: 'Tripledart',
    value: 'QUOTE_TRIPLEDART',
  },
  {
    title: 'Affinity',
    value: QUOTE_AFFINITY,
  },
  {
    title: 'Affinity - March 2024',
    value: QUOTE_AFFINITY_MARCH_2024,
  },
  {
    title: 'Materialize',
    value: QUOTE_MATERIALIZE,
  },
  {
    title: 'Middesk',
    value: QUOTE_MIDDESK,
  },
  {
    title: 'Plotline',
    value: QUOTE_PLOTLINE,
  },
  {
    title: 'Danfoss EU',
    value: QUOTE_DANFOSS_EU,
  },
  {
    title: 'Danfoss US',
    value: QUOTE_DANFOSS_US,
  },
  {
    title: 'Lender',
    value: QUOTE_LENDER,
  },
  {
    title: 'Siraya',
    value: QUOTE_SIRAYA,
  },
  {
    title: 'Sentry',
    value: QUOTE_SENTRY,
  },
  {
    title: 'StarTree',
    value: QUOTE_STARTREE,
  },
  {
    title: 'Proper',
    value: QUOTE_PROPER,
  },
  {
    title: 'Teleport',
    value: QUOTE_TELEPORT,
  },
  {
    title: 'Pave',
    value: QUOTE_PAVE,
  },
  {
    title: 'Pave ARR',
    value: QUOTE_PAVE_ARR,
  },
  {
    title: 'Xage',
    value: QUOTE_XAGE,
  },
  {
    title: 'Schoox',
    value: QUOTE_SCHOOX,
  },
  {
    title: 'Descope',
    value: QUOTE_DESCOPE,
  },
  {
    title: 'AcuityMD',
    value: QUOTE_ACUITYMD,
  },
  { title: 'Default V2 (Deprecated)', value: 'QUOTE_DEFAULT_1_TEST_V2' },
  {
    title: 'Default - Services Order Form V2 (Deprecated)',
    value: 'QUOTE_DEFAULT_2_TEST_V2',
  },
  {
    title: 'Clay',
    value: 'QUOTE_CLAY',
  },
  {
    title: 'Centerbase',
    value: 'QUOTE_CENTERBASE_LEGALFIT',
  },
  {
    title: 'Legalfit',
    value: 'QUOTE_LEGALFIT',
  },
  {
    title: 'Vividly',
    value: 'QUOTE_VIVIDLY',
  },
  {
    title: 'Aerospike',
    value: 'QUOTE_AEROSPIKE',
  },
  {
    title: 'Mercell',
    value: 'QUOTE_MERCELL',
  },
  {
    title: 'Endor Labs',
    value: 'QUOTE_ENDOR_LABS',
  },
  {
    title: 'Optio',
    value: QUOTE_OPTIO,
  },
  {
    title: 'Coast',
    value: QUOTE_COAST,
  },
  {
    title: 'Lifted',
    value: QUOTE_LIFTED,
  },
  {
    title: 'First Due',
    value: QUOTE_FIRST_DUE,
  },
  {
    title: 'Appcues',
    value: QUOTE_APP_CUES,
  },
  {
    title: 'ENTR',
    value: QUOTE_ENTR,
  },
  {
    title: 'Rillet',
    value: QUOTE_RILLET,
  },
  {
    title: 'Tempo',
    value: QUOTE_TEMPO,
  },
  {
    title: 'Fexa',
    value: QUOTE_FEXA,
  },
  {
    title: 'AgentSync',
    value: QUOTE_AGENT_SYNC,
  },
  {
    title: 'MonetizeNow',
    value: QUOTE_MONETIZENOW,
  },
];

/** Quote template value -> display title, for read-only labels (e.g. list cells). */
export const QUOTE_TEMPLATE_DISPLAY: Record<string, string> =
  Object.fromEntries(
    QUOTE_TEMPLATE_ITEMS.map(({ value, title }) => [value, title]),
  );

export const SCHEDULED_OFFERING_OPTIONS: {
  title: string;
  value: EffectiveDateType;
}[] = [
  { title: '+ 1 Month', value: 1 },
  { title: '+ 3 Month', value: 3 },
  { title: '+ 6 Month', value: 6 },
  { title: '+ 1 Year', value: 12 },
  { title: 'Custom', value: 'CUSTOM' },
];

export const QUOTE_START_DATE_SOURCE_OPTIONS = [
  {
    title: 'Editable until Drafted',
    value: QuoteStartDateSourceEnum.ORIGINAL_CONTRACT_START,
  },
  {
    title: `Set by Signature Date`,
    value: QuoteStartDateSourceEnum.AT_TIME_OF_SIGNING,
  },
  {
    title: 'Editable after Signature',
    value: QuoteStartDateSourceEnum.MANUAL,
  },
];

export const DELAYED_BILLING_QUOTE_START_SOURCE = new Set([
  QuoteStartDateSourceEnum.AT_TIME_OF_SIGNING,
  QuoteStartDateSourceEnum.MANUAL,
]);

export const QUOTE_PROGRESS_CANCEL_MESSAGE = {
  [QuoteCancelationReasonEnum.BY_USER]: 'Canceled by User',
  [QuoteCancelationReasonEnum.CUSTOMER_DECLINED]: 'Canceled by Customer',
  [QuoteCancelationReasonEnum.DUPLICATE]: 'Canceled due to duplication',
  [QuoteCancelationReasonEnum.EXPIRED]: 'Expired due to Inactivity',
  [QuoteCancelationReasonEnum.SUPERSEDED_BY_PROCESSED_QUOTE]:
    'Superseded by Processed Quote',
  [QuoteCancelationReasonEnum.BY_MIGRATION]: 'Canceled due to Migration',
};

export const QUOTE_EXPIRATION_DATE_EDITABLE_STATUSES = [
  QuoteStatusEnum.REVIEW,
  QuoteStatusEnum.SENT,
  QuoteStatusEnum.APPROVED,
  QuoteStatusEnum.EXPIRED,
];

export const DISCOUNT_BREAKDOWN_ROWS: Array<{
  name: string;
  field: DiscountBreakdownField;
}> = [
  { name: 'Custom Discounts', field: 'calculatedCustomDiscountAmount' },
  { name: 'Standard Discounts', field: 'calculatedCatalogDiscountAmount' },
  { name: 'Onetime Discounts', field: 'onetimeDiscountProductAmount' },
];
